/**
 * Returns all defined entries of the given object whose keys match the given
 * list of keys.
 *
 * In the example below, property `b` is included because it exists in the
 * object with value `undefined`. Property 'c' is omitted because its key is not
 * specified in the list of properties to include.
 *
 * filterExistingEntries(
 *  { a: 'A', b: undefined, c: 'C' },
 *  ['a', 'b']
 * ) // Returns { a: 'A', b: 'undefined }
 *
 * The example below would cause a type error because there is no property with
 * key 'b' in the given object.
 *
 * filterExistingEntries(
 *  { a: 'A' },
 *  ['a', 'b'] // Type error here
 * )
 *
 * In the example below, no list of keys is specified. This has the effect of
 * returning all existing keys. (A empty function.)
 * filterExistingEntries(
 *  { a: 'A', 'b': 'B' }
 * ) // Returns { a: 'A', 'b': 'B' }
 */
export function filterExistingEntries<
    Unfiltered extends object,
    Filter extends keyof Unfiltered,
    Filtered extends { [P in Filter]: Unfiltered[Filter] },
>(entries: Unfiltered, keys: Filter[] = Object.keys(entries) as Filter[]): Filtered {
    return keys.reduce(
        (filtered, id) => (id in entries ? { ...filtered, [id]: entries[id] } : filtered),
        {} as Filtered,
    );
}
